/**
 * DraftingCustomizePanel - the customization workspace for an existing draft
 * or strategy. Opened from DraftingDetail via
 * `dispatch(showSidePanel({ type: 'draftingCustomize', … }))`.
 *
 * Three tabs:
 *   • Customization - the full generation recipe (description, tone,
 *     instructions, focus areas, and matter-context toggles when the draft is
 *     bound to a matter). Pre-filled from the backend's persisted recipe.
 *   • Attachments   - the reference files that inform generation. Read (open),
 *     remove, and add (queue then upload). Drafts only (insights have no
 *     reference-file store yet).
 *   • Playbook check - map the draft against a playbook (planned feature).
 *     (Named "Playbook check", not "Revision", so it doesn't collide with the
 *     version-history "Revisions" concept - those are different things: this is a
 *     compliance pass against a playbook; that is time-travel over saved snapshots.)
 *
 * Version history is a PANE on the Drafting detail surface, not a tab here.
 * This panel is deliberately still a `GlobalSidePanel` - it is a task you
 * finish and dismiss, not a surface you arrange - so it brings no chrome of
 * its own: the panel draws the glass, the header and the close button.
 * The footer Regenerate button submits the edited recipe; the AI engine
 * re-reads the current attachment set from blob, so add/remove here is
 * honoured on the next generation. Creation is handled by DraftingWizard.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiSlidersHorizontalLight,
  PiArrowClockwiseLight,
  PiFileTextLight,
  PiSparkleLight,
  PiPaperclipLight,
  PiBookOpenTextLight,
  PiBriefcaseLight,
  PiTrashLight,
  PiEyeLight,
  PiUploadSimpleLight,
  PiPaletteLight,
} from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import {
  generateDrafting,
  fetchDraftingGenerateOptions,
  fetchDraftingFiles,
  uploadDraftingFile,
  deleteDraftingFile,
  fetchDraftingContent,
  DraftingKind,
  DraftingFile,
  MatterContextOptions,
} from '../../store/slices/draftingSlice';
import BrandingPreview from '../Templates/BrandingPreview';
import { fetchBrandingTemplates, getBrandingTemplate } from '../../store/actions/TemplateActions';
import apis from '../../utils/apis';
import { useHistory } from 'react-router-dom';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { startReviewFromDraft } from '../../utils/reviewFromDocApi';
import Textarea from '../shared/TailwindComponents/Textarea';
import Input from '../shared/TailwindComponents/Input';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Button from '../shared/TailwindComponents/Button';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { Well } from '../shared/brand/Field';
import { FIELD_LABEL_CLASS, FIELD_HELP_CLASS, FIELD_GAP } from '../shared/TailwindComponents/FieldShell';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import CustomFileReader from '../shared/CustomFileReader';
import MatterContextStep, { DEFAULT_MATTER_CONTEXT_OPTIONS } from './wizard/MatterContextStep';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface DraftingCustomizePanelProps {
  kind: DraftingKind;
  draftingUuid?: string;
  existingItem?: Record<string, any>;
  onClose: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TONE_OPTIONS = [
  { value: '',            label: 'Default (Professional)' },
  { value: 'formal',      label: 'Formal & Authoritative' },
  { value: 'persuasive',  label: 'Persuasive & Compelling' },
  { value: 'neutral',     label: 'Neutral & Objective' },
  { value: 'concise',     label: 'Concise & Direct' },
  { value: 'detailed',    label: 'Detailed & Comprehensive' },
  { value: 'empathetic',  label: 'Empathetic & Client-Focused' },
];

const ATTACHMENT_EXTENSIONS = [
  'pdf', 'docx', 'txt', 'md', 'csv', 'tsv', 'json', 'yaml', 'yml',
  'xml', 'xlsx', 'pptx', 'png', 'jpg', 'jpeg', 'tiff', 'tif', 'bmp', 'webp',
];
const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

type WorkspaceTab = 'customize' | 'matter_context' | 'attachments' | 'branding' | 'revision';

const fmtBytes = (n: number | null): string => {
  if (n == null) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// ─── Component ────────────────────────────────────────────────────────────────

const DraftingCustomizePanel: React.FC<DraftingCustomizePanelProps> = ({
  kind,
  draftingUuid,
  existingItem,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [startingCheck, setStartingCheck] = useState(false);

  const kindLabel = kind === 'draft' ? t('draft') : t('strategy');

  // Playbook check - run a playbook compliance pass on THIS draft. It's a normal
  // review under the hood (reuses findings/redlines/versioning) that also links
  // back to the draft, so it surfaces in the draft's intelligence panel. Opens the
  // shared playbook picker, then starts the review and jumps to it.
  const startPlaybookCheck = useCallback(() => {
    if (!draftingUuid) return;
    dispatch(showModal({
      showModal: true,
      type: 'playbookPicker',
      message: '',
      selectedPlaybookUuid: '',
      onPlaybookSelect: async (playbook: { uuid: string } | null) => {
        if (!playbook || startingCheck) return;
        setStartingCheck(true);
        try {
          const res = await startReviewFromDraft(draftingUuid, playbook.uuid);
          if (res?.session_uuid) {
            dispatch(showFeedbackModal({
              showModal: true, modalType: 'success', duration: 5000,
              message: t('Playbook check started — opening the review.'),
            }));
            onClose?.();
            history.push(`/reviews/${res.session_uuid}`);
          } else {
            dispatch(showFeedbackModal({
              showModal: true, modalType: 'error', duration: 6000,
              message: res?.error || t('Could not start the playbook check.'),
            }));
          }
        } catch (e: any) {
          dispatch(showFeedbackModal({
            showModal: true, modalType: 'error', duration: 6000,
            message: e?.response?.data?.error || t('Could not start the playbook check.'),
          }));
        } finally {
          setStartingCheck(false);
        }
      },
    }));
  }, [draftingUuid, dispatch, t, history, onClose, startingCheck]);

  const matterUuid = (existingItem?.matter_uuid as string | undefined) || undefined;
  const matterJurisdiction = (existingItem?.matter_jurisdiction as string | undefined) || undefined;
  // Reference files only exist for drafts today.
  const supportsFiles = kind === 'draft';

  // ── Recipe state ──────────────────────────────────────────────────────────
  const [description, setDescription] = useState<string>((existingItem?.description as string) ?? '');
  const [tone, setTone] = useState('');
  const [instructions, setInstructions] = useState('');
  const [focusAreasText, setFocusAreasText] = useState('');
  const [matterContext, setMatterContext] = useState<MatterContextOptions>(
    matterUuid ? { ...DEFAULT_MATTER_CONTEXT_OPTIONS } : {},
  );

  // ── Attachments state ───────────────────────────────────────────────────────
  const [files, setFiles] = useState<DraftingFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Files added to the uploader but not yet sent - uploaded on explicit click.
  const [queuedFiles, setQueuedFiles] = useState<File[]>([]);
  // Filename currently being deleted, for a per-row spinner.
  const [deletingFile, setDeletingFile] = useState<string | null>(null);

  // ── Branding state ──────────────────────────────────────────────────────────
  const [brandingOptions, setBrandingOptions] = useState<{ value: string; label: string }[]>([]);
  const [brandingTemplateId, setBrandingTemplateId] = useState<string>((existingItem?.branding_template_id as string) || '');
  const [brandingConfig, setBrandingConfig] = useState<any>(null);
  const [draftMarkdown, setDraftMarkdown] = useState('');
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [applyingBranding, setApplyingBranding] = useState(false);

  const [activeTab, setActiveTab] = useState<WorkspaceTab>('customize');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const toneDropdownOptions = useMemo(
    () => TONE_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) })),
    [t],
  );

  // ── Loaders ────────────────────────────────────────────────────────────────
  const loadFiles = useCallback(async () => {
    if (!draftingUuid || !supportsFiles) return;
    setFilesLoading(true);
    const res = await dispatch(fetchDraftingFiles({ kind, uuid: draftingUuid }));
    if (fetchDraftingFiles.fulfilled.match(res)) setFiles(res.payload);
    setFilesLoading(false);
  }, [dispatch, kind, draftingUuid, supportsFiles]);

  // Pre-fill the recipe from the backend's persisted options + load files.
  useEffect(() => {
    if (!draftingUuid) return;
    let alive = true;
    (async () => {
      const res = await dispatch(fetchDraftingGenerateOptions({ kind, uuid: draftingUuid }));
      if (alive && fetchDraftingGenerateOptions.fulfilled.match(res)) {
        const o = res.payload || {};
        if (o.tone) setTone(o.tone);
        if (o.instructions) setInstructions(o.instructions);
        if (o.additional_context) setDescription(o.additional_context);
        if (Array.isArray(o.focus_areas) && o.focus_areas.length) {
          setFocusAreasText(o.focus_areas.join(', '));
        }
        if (matterUuid && o.matter_context_options && Object.keys(o.matter_context_options).length) {
          setMatterContext(o.matter_context_options);
        }
      }
    })();
    loadFiles();
    return () => { alive = false; };
  }, [dispatch, kind, draftingUuid, matterUuid, loadFiles]);

  // ── Branding: lazy-load templates + this draft's content when the tab opens ──
  useEffect(() => {
    if (activeTab !== 'branding' || !draftingUuid) return;
    let alive = true;
    (async () => {
      setBrandingLoading(true);
      try {
        if (brandingOptions.length === 0) {
          const list: any = await dispatch(fetchBrandingTemplates());
          const rows: any[] = Array.isArray(list) ? list : (list?.templates || list?.data || []);
          if (alive) {
            setBrandingOptions(
              rows
                .map((tpl: any) => ({
                  value: tpl.id ?? tpl.uuid ?? tpl.template_id,
                  label: tpl.TemplateName ?? tpl.name ?? tpl.template_name ?? t('Untitled template'),
                }))
                .filter((o: any) => o.value),
            );
          }
        }
        if (!draftMarkdown) {
          const res = await dispatch(fetchDraftingContent({ kind, uuid: draftingUuid }));
          if (alive && fetchDraftingContent.fulfilled.match(res)) {
            setDraftMarkdown(res.payload.content || '');
          }
        }
      } finally {
        if (alive) setBrandingLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [activeTab, draftingUuid, kind, dispatch, t, brandingOptions.length, draftMarkdown]);

  const onSelectBranding = useCallback(async (id: string) => {
    setBrandingTemplateId(id);
    if (!id) { setBrandingConfig(null); return; }
    setBrandingLoading(true);
    try {
      const tpl: any = await dispatch(getBrandingTemplate(id));
      setBrandingConfig(tpl?.Branding ?? tpl?.branding ?? null);
    } finally {
      setBrandingLoading(false);
    }
  }, [dispatch]);

  // Load the config for an already-attached template so the preview shows it.
  useEffect(() => {
    if (activeTab !== 'branding' || !brandingTemplateId || brandingConfig) return;
    let alive = true;
    (async () => {
      const tpl: any = await dispatch(getBrandingTemplate(brandingTemplateId));
      if (alive) setBrandingConfig(tpl?.Branding ?? tpl?.branding ?? null);
    })();
    return () => { alive = false; };
  }, [activeTab, brandingTemplateId, brandingConfig, dispatch]);

  // Persist the attached branding on the draft (no regeneration). Applied by the
  // backend when the document is downloaded/exported as DOCX.
  const applyBranding = useCallback(async () => {
    if (!draftingUuid) return;
    setApplyingBranding(true);
    try {
      await apis.patch(`/drafting/drafts/${draftingUuid}`, { branding_template_id: brandingTemplateId || null });
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'success', duration: 3500,
        message: brandingTemplateId
          ? t('Branding applied — it is used when you download or export this document.')
          : t('Branding cleared.'),
      }));
    } catch (e: any) {
      dispatch(showFeedbackModal({
        showModal: true, modalType: 'error', duration: 4000,
        message: e?.response?.data?.error || t('Could not apply branding.'),
      }));
    } finally {
      setApplyingBranding(false);
    }
  }, [draftingUuid, brandingTemplateId, dispatch, t]);

  // ── Attachment actions ──────────────────────────────────────────────────────
  // Files are staged in the uploader first; the user clicks Upload to send them.
  const handleUploadQueued = useCallback(async () => {
    if (!draftingUuid || queuedFiles.length === 0) return;
    setUploading(true);
    let anyFailed = false;
    for (const file of queuedFiles) {
      const res = await dispatch(uploadDraftingFile({ kind, uuid: draftingUuid, file }));
      if (!uploadDraftingFile.fulfilled.match(res)) {
        anyFailed = true;
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: (res.payload as string) || t('Failed to upload {{name}}', { name: file.name }),
          showModal: true, duration: 4000,
        }));
      }
    }
    setUploading(false);
    setQueuedFiles([]);
    await loadFiles();
    if (!anyFailed) {
      dispatch(showFeedbackModal({
        modalType: 'success', message: t('Attachment(s) added.'),
        showModal: true, duration: 2500,
      }));
    }
  }, [dispatch, kind, draftingUuid, queuedFiles, loadFiles, t]);

  const removeFile = useCallback(async (filename: string) => {
    if (!draftingUuid) return;
    setDeletingFile(filename);
    const res = await dispatch(deleteDraftingFile({ kind, uuid: draftingUuid, filename }));
    setDeletingFile(null);
    if (deleteDraftingFile.fulfilled.match(res)) {
      setFiles((prev) => prev.filter((f) => f.filename !== filename));
      dispatch(showFeedbackModal({
        modalType: 'success', message: t('Attachment removed.'),
        showModal: true, duration: 2000,
      }));
    } else {
      dispatch(showFeedbackModal({
        modalType: 'error', message: (res.payload as string) || t('Failed to remove attachment.'),
        showModal: true, duration: 4000,
      }));
    }
  }, [dispatch, kind, draftingUuid, t]);

  const handleDeleteFile = useCallback((filename: string) => {
    dispatch(showModal({
      type: 'confirmation',
      showModal: true,
      message: t('Remove attachment'),
      subMsg: t('Remove "{{name}}" from this draft? It will no longer inform generation.', { name: filename }),
      isDest: true,
      icon: <PiTrashLight className="h-6 w-6 text-danger-600" />,
      onConfirm: async () => {
        dispatch(hideModal());
        await removeFile(filename);
      },
      onCancel: () => dispatch(hideModal()),
    }));
  }, [dispatch, removeFile, t]);

  const handleOpenFile = useCallback((filename: string) => {
    if (!draftingUuid) return;
    // Open in our own GlobalModal attachment viewer. CustomFileReader fetches
    // the (auth'd) API path itself and renders per type - PDF, DOCX (mammoth),
    // images, spreadsheets, and plain text - with a download fallback.
    const path = kind === 'draft' ? 'drafts' : 'insights';
    const fileUrl = `/drafting/${path}/${draftingUuid}/files/${encodeURIComponent(filename)}`;
    dispatch(showModal({
      type: 'attachmentViewer',
      showModal: true,
      message: filename,
      icon: <PiFileTextLight className="h-5 w-5 text-primary-600" />,
      component: <CustomFileReader fileUrl={fileUrl} filename={filename} />,
    }));
  }, [dispatch, kind, draftingUuid]);

  // ── Regenerate ────────────────────────────────────────────────────────────
  const runGenerate = useCallback(async (force: boolean) => {
    if (!draftingUuid) return;
    const focus_areas = focusAreasText
      .split(',').map((s) => s.trim()).filter(Boolean);

    const result = await dispatch(generateDrafting({
      kind,
      uuid: draftingUuid,
      tone: tone || undefined,
      instructions: instructions || undefined,
      additional_context: description || undefined,
      focus_areas: focus_areas.length ? focus_areas : undefined,
      matter_context_options: matterUuid ? matterContext : undefined,
      branding_template_id: brandingTemplateId || undefined,
      force,
    }));

    if (generateDrafting.fulfilled.match(result)) {
      if (result.payload.unchanged) {
        // Nothing changed since the last version - warn and let them force it.
        setIsSubmitting(false);
        submittingRef.current = false;
        dispatch(showModal({
          type: 'confirmation',
          showModal: true,
          message: t('No changes detected'),
          subMsg: t('Nothing has changed since the last version (same recipe and attachments). Regenerate anyway?'),
          icon: <PiArrowClockwiseLight className="h-6 w-6 text-primary-600" />,
          onConfirm: async () => {
            dispatch(hideModal());
            submittingRef.current = true;
            setIsSubmitting(true);
            await runGenerate(true);
          },
          onCancel: () => dispatch(hideModal()),
        }));
        return;
      }
      dispatch(showFeedbackModal({
        modalType: 'success', message: t('Regeneration queued.'),
        showModal: true, duration: 3000,
      }));
      onClose();
    } else {
      dispatch(showFeedbackModal({
        modalType: 'error', message: (result.payload as string) || t('Regeneration failed.'),
        showModal: true, duration: 4000,
      }));
      setIsSubmitting(false);
      submittingRef.current = false;
    }
  }, [dispatch, kind, draftingUuid, description, tone, instructions, focusAreasText,
      matterUuid, matterContext, brandingTemplateId, onClose, t]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draftingUuid) {
      dispatch(showFeedbackModal({
        modalType: 'error', message: t('No document selected for regeneration.'),
        showModal: true, duration: 3000,
      }));
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    await runGenerate(false);
  }, [dispatch, draftingUuid, runGenerate, t]);

  // ── Tabs (pill style, matching the Project Resources side panel) ─────────────
  const tabs = useMemo(() => {
    const base: { value: WorkspaceTab; label: string; icon: React.ReactNode; count?: number }[] = [
      { value: 'customize', label: t('Customization'), icon: <PiSlidersHorizontalLight className="w-4 h-4" /> },
    ];
    if (matterUuid) {
      base.push({ value: 'matter_context', label: t('Matter context'), icon: <PiBriefcaseLight className="w-4 h-4" /> });
    }
    if (supportsFiles) {
      base.push({ value: 'attachments', label: t('Attachments'), icon: <PiPaperclipLight className="w-4 h-4" />, count: files.length });
    }
    if (kind === 'draft') {
      base.push({ value: 'branding', label: t('Branding'), icon: <PiPaletteLight className="w-4 h-4" /> });
    }
    base.push({ value: 'revision', label: t('Playbook check'), icon: <PiBookOpenTextLight className="w-4 h-4" /> });
    return base;
  }, [supportsFiles, matterUuid, files.length, kind, t]);

  return (
    /* No surface of its own: the global side panel is already the glass box.
       A `bg-white dark:bg-gray-800` root here painted over it. */
    <div className="flex h-full flex-col">
      {/* Tab pills - the shared nav-pill treatment, not a local recipe. */}
      <div className="shrink-0 border-b border-hairline-soft px-5 py-3">
        <div className="glass-bar inline-flex flex-wrap items-center gap-0.5 rounded-full p-0.5">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
              aria-pressed={activeTab === tab.value}
              className={`nav-pill flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs ${
                activeTab === tab.value
                  ? 'nav-pill-active font-medium text-primary-600 dark:text-primary-300'
                  : 'text-fg-body'
              }`}
            >
              {tab.icon}
              {tab.label}
              {typeof tab.count === 'number' && tab.count > 0 && (
                <span className="ms-0.5 rounded-full bg-surface-sunken px-1.5 text-[10px] font-medium text-fg-body">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {/* ── Customization tab ── */}
          {activeTab === 'customize' && (
            <div className="space-y-5">
              <div className="flex items-start gap-2.5 rounded-lg border border-warning-200 bg-warning-50 p-3.5 text-xs leading-relaxed text-warning-800 dark:border-warning-700/50 dark:bg-warning-900/20 dark:text-warning-200">
                <PiSparkleLight size={14} className="mt-0.5 shrink-0" />
                <span>
                  {t('Regeneration replaces the current content of this {{kind}}. Previous versions stay available under Version history.', { kind: kindLabel })}
                </span>
              </div>

              <Textarea
                id="regen-description"
                label={t('Description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('Describe what the document should cover or any changes from the current version…')}
                rows={5}
                disabled={isSubmitting}
              />

              <div>
                <Dropdown
                  label={t('Tone & style')}
                  options={toneDropdownOptions}
                  value={tone}
                  onChange={(val) => setTone(String(val))}
                  fullWidth
                  useApi={false}
                  placeholder={t('Professional (default)')}
                />
              </div>

              <Textarea
                id="regen-instructions"
                label={t('Custom instructions')}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder={t('Optional: specific sections to include, jurisdiction, emphasis areas, parties…')}
                rows={4}
                disabled={isSubmitting}
              />

              <Input
                type="text"
                id="regen-focus"
                label={t('Focus areas')}
                value={focusAreasText}
                onChange={(e) => setFocusAreasText(e.target.value)}
                placeholder={t('Comma-separated, e.g. liability, termination, IP ownership')}
                disabled={isSubmitting}
              />

            </div>
          )}

          {/* ── Matter context tab (only when a matter is connected) ── */}
          {activeTab === 'matter_context' && matterUuid && (
            <div className="space-y-3">
              <div className="flex items-start gap-2.5 rounded-lg border border-info-200 bg-info-50 p-3.5 text-xs leading-relaxed text-info-700 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300">
                <PiBriefcaseLight size={14} className="mt-0.5 shrink-0" />
                <span>
                  {t('This draft is connected to a matter. Choose which slices of the matter’s context to feed the model when regenerating. All on by default; toggle off what you don’t want.')}
                </span>
              </div>
              <MatterContextStep
                matterUuid={matterUuid}
                matterJurisdiction={matterJurisdiction}
                hasAttachments={files.length > 0}
                value={matterContext}
                onChange={setMatterContext}
              />
            </div>
          )}

          {/* ── Attachments tab ── */}
          {activeTab === 'attachments' && supportsFiles && (
            <div className="space-y-4">
              <div className="flex items-start gap-2.5 rounded-lg border border-info-200 bg-info-50 p-3.5 text-xs leading-relaxed text-info-700 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300">
                <PiPaperclipLight size={14} className="mt-0.5 shrink-0" />
                <span>
                  {t('These reference files inform generation. Changes here take effect the next time you regenerate. Large files are summarized server-side.')}
                </span>
              </div>

              {filesLoading ? (
                /* An ordinary list load, not an AI process - a skeleton, not
                   the logo loader. */
                <Skeleton variant="list" rows={3} />
              ) : files.length === 0 ? (
                <p className="py-3 text-sm text-fg-muted">{t('No reference files yet.')}</p>
              ) : (
                <ul className="space-y-2">
                  {files.map((f) => (
                    <li key={f.filename}>
                      <Well className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <PiFileTextLight size={18} className="shrink-0 text-primary-500" />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-fg-strong">{f.filename}</p>
                            {f.size_bytes != null && (
                              <p className={FIELD_HELP_CLASS}>{fmtBytes(f.size_bytes)}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <Button
                            variant="neutral"
                            size="small"
                            onClick={() => handleOpenFile(f.filename)}
                            tooltip={t('Open')}
                            ariaLabel={t('Open')}
                            icon={<PiEyeLight size={16} />}
                          />
                          <Button
                            variant="neutral"
                            size="small"
                            onClick={() => handleDeleteFile(f.filename)}
                            disabled={deletingFile === f.filename}
                            isLoading={deletingFile === f.filename}
                            tooltip={t('Remove')}
                            ariaLabel={t('Remove')}
                            icon={deletingFile === f.filename ? undefined : <PiTrashLight size={16} />}
                          />
                        </div>
                      </Well>
                    </li>
                  ))}
                </ul>
              )}

              <div>
                <p className={`${FIELD_LABEL_CLASS} ${FIELD_GAP}`}>
                  {t('Add files')}
                </p>
                {/* Stage in the uploader, then click Upload - nothing is sent
                    until the user confirms. */}
                <FileUploader
                  files={queuedFiles}
                  onFilesChange={setQueuedFiles}
                  allowedExtensions={ATTACHMENT_EXTENSIONS}
                  maxSizeBytes={ATTACHMENT_MAX_BYTES}
                />
                {queuedFiles.length > 0 && (
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <span className={FIELD_HELP_CLASS}>
                      {t('{{n}} file(s) ready to upload', { n: queuedFiles.length })}
                    </span>
                    <Button
                      type="button"
                      variant="primary"
                      size="small"
                      isLoading={uploading}
                      icon={uploading ? undefined : <PiUploadSimpleLight size={14} />}
                      onClick={handleUploadQueued}
                    >
                      {uploading
                        ? t('Uploading…')
                        : t('Upload {{n}} file(s)', { n: queuedFiles.length })}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Branding tab - pick a template, preview THIS document under it ── */}
          {activeTab === 'branding' && (
            <div className="space-y-4">
              <div className="flex items-start gap-2.5 rounded-lg border border-info-200 bg-info-50 p-3.5 text-xs leading-relaxed text-info-700 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300">
                <PiPaletteLight size={14} className="mt-0.5 shrink-0" />
                <span>
                  {t('Pick a branding template to see how this document looks under it — fonts, margins, headers and page layout. The selected branding is applied when you regenerate or export (it is deterministic, no AI needed).')}
                </span>
              </div>

              <Dropdown
                label={t('Branding template')}
                options={brandingOptions}
                value={brandingTemplateId}
                onChange={(val) => onSelectBranding(String(val))}
                placeholder={t('Default (no branding)')}
                useApi={false}
                fullWidth
              />

              <div className="flex items-center justify-between gap-3">
                <span className={FIELD_HELP_CLASS}>
                  {t('Applied when you download or export this document — no regeneration.')}
                </span>
                <Button
                  type="button"
                  variant="primary"
                  size="small"
                  isLoading={applyingBranding}
                  disabled={applyingBranding}
                  onClick={applyBranding}
                  icon={<PiPaletteLight size={14} />}
                >
                  {t('Apply branding')}
                </Button>
              </div>

              <div>
                <p className={`${FIELD_LABEL_CLASS} ${FIELD_GAP}`}>{t('Live preview')}</p>
                {brandingLoading ? (
                  <Skeleton variant="detail" />
                ) : !brandingTemplateId ? (
                  <p className="py-3 text-sm text-fg-muted">
                    {t('Select a branding template above to preview this document with it applied.')}
                  </p>
                ) : !draftMarkdown ? (
                  <p className="py-3 text-sm text-fg-muted">
                    {t('This document has no generated content to preview yet.')}
                  </p>
                ) : (
                  <div className="rounded-lg border border-hairline bg-surface-sunken/40 p-3">
                    <BrandingPreview branding={brandingConfig} content={draftMarkdown} />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Revision tab (playbook mapping - planned) ── */}
          {activeTab === 'revision' && (
            <div className="space-y-4">
              <div className="flex items-start gap-2.5 rounded-lg border border-info-200 bg-info-50 p-3.5 text-xs leading-relaxed text-info-700 dark:border-info-800 dark:bg-info-900/20 dark:text-info-300">
                <PiBookOpenTextLight size={14} className="mt-0.5 shrink-0" />
                <span>
                  {t('Map this draft against a playbook — score each section for compliance, surface gaps and risky clauses, and regenerate with the fixes applied.')}
                </span>
              </div>

              <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-hairline py-10 text-center">
                <PiBookOpenTextLight size={30} className="text-fg-muted" />
                <div>
                  <p className="text-sm font-medium text-fg-strong">
                    {t('Check this draft against a playbook')}
                  </p>
                  <p className={`mx-auto mt-1 max-w-xs ${FIELD_HELP_CLASS}`}>
                    {existingItem?.status === 'completed'
                      ? t('Pick a playbook to run a compliance pass. Findings and suggested redlines open in a review, and the result is tracked in this draft’s intelligence panel.')
                      : t('Generate the draft first — a playbook check runs against its generated content.')}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="small"
                  onClick={startPlaybookCheck}
                  isLoading={startingCheck}
                  disabled={startingCheck || existingItem?.status !== 'completed'}
                  icon={<PiBookOpenTextLight className="w-4 h-4" />}
                >
                  {t('Select a playbook')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer - regenerate is always available (Close lives in the panel header) */}
        <form onSubmit={handleSubmit} className="shrink-0 border-t border-hairline-soft">
          <div className="flex items-center justify-end gap-3 px-5 py-4">
            <Button
              type="submit"
              variant="primary"
              size="small"
              isLoading={isSubmitting}
              icon={isSubmitting ? undefined : <PiArrowClockwiseLight size={14} />}
              disabled={!draftingUuid || isSubmitting}
            >
              {isSubmitting ? t('Queuing…') : t('Regenerate')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DraftingCustomizePanel;
